import { getLogger } from '@trimble/logging';

interface TaskDef<T> {
  key: string;
  task(): Promise<T>;
  cancelable: boolean;
  abortController: AbortController;
  /*
   * A simple priority system where lower numbers indicate higher priority
   * */
  priority(): number;
}

interface TaskDefWrapper<T> extends TaskDef<T> {
  taskWrapper(): Promise<void>;
}

const DEFAULT_CONCURRENCY = 8;
const TASK_TIMEOUT_MS = 10_000;

interface PendingPromise<T> {
  promise: Promise<T>;
  reject(reason: Error): void;
}

const createAbortError = (): DOMException =>
  new DOMException('The operation was aborted.', 'AbortError');

export class PromiseQueue<T> {
  private queue: TaskDefWrapper<T>[] = [];
  private activeCount = 0;
  private readonly activeTasks: Map<string, TaskDefWrapper<T>> = new Map();
  private readonly pendingPromises = new Map<string, PendingPromise<T>>();
  private readonly concurrency: number;

  /*
   * @param type - Type of items processed by the queue
   * @param concurrency - Maximum number of concurrent tasks (default: 8)
   * */
  constructor(
    concurrency = DEFAULT_CONCURRENCY,
    public type: string = 'default'
  ) {
    this.concurrency = concurrency;
  }

  public static genKey(type: string, x: number, y: number, z: number): string {
    return `${type}:x${x}-y${y}-z${z}`;
  }

  public get length(): number {
    return this.queue.length;
  }

  public get activeLength(): number {
    return this.activeCount;
  }

  private rejectPending(key: string, reason: Error): void {
    const pending = this.pendingPromises.get(key);
    if (!pending) {
      return;
    }

    pending.reject(reason);
    this.pendingPromises.delete(key);
  }

  private rejectAllPending(reason: Error): void {
    for (const key of [...this.pendingPromises.keys()]) {
      this.rejectPending(key, reason);
    }
  }

  public removeWhere(predicate: (task: TaskDefWrapper<T>) => boolean): void {
    const tasksToKeep: TaskDefWrapper<T>[] = [];
    for (const task of this.queue) {
      if (predicate(task) && task.cancelable) {
        task.abortController.abort();
        this.rejectPending(task.key, createAbortError());
      } else {
        tasksToKeep.push(task);
      }
    }
    this.queue = tasksToKeep;

    const activeTasksToAbort = Array.from(this.activeTasks.values()).filter(
      (t) => predicate(t) && t.cancelable
    );
    for (const task of activeTasksToAbort) {
      task.abortController.abort();
    }
  }

  public remove(key: string): void {
    const queued = this.queue.filter((t) => t.key === key && t.cancelable);
    if (queued.length > 0) {
      this.queue = this.queue.filter((t) => t.key !== key);
      queued.forEach((t) => t.abortController.abort());
    }
    const activeTask = this.activeTasks.get(key);
    if (activeTask && activeTask.cancelable) {
      activeTask.abortController.abort();
    }
    this.rejectPending(key, createAbortError());
  }

  public clear(): void {
    const tasks = [...this.queue];
    this.queue = [];
    const activeTasks = [...this.activeTasks.values()];
    [...tasks, ...activeTasks].forEach((task) => {
      task.abortController.abort();
    });
    this.rejectAllPending(createAbortError());
    this.activeTasks.clear();
  }

  public add(taskDef: TaskDef<T>): Promise<T> {
    // cache the pending promise for this task key, so if the same task is added again before it runs, we can return the same promise instead of adding a duplicate task to the queue.
    const existing = this.pendingPromises.get(taskDef.key);
    if (existing) {
      return existing.promise;
    }

    let rejectPending!: (reason: Error) => void;
    const addPromise = new Promise<T>((resolve, reject) => {
      rejectPending = reject;
      const taskDefWrapper: TaskDefWrapper<T> = {
        ...taskDef,
        taskWrapper: (): Promise<void> => {
          this.activeCount++;
          this.activeTasks.set(taskDef.key, taskDefWrapper);

          const timeoutSymbol = Symbol('timeout');
          let timeoutId: ReturnType<typeof setTimeout>;
          const timeoutPromise = new Promise<symbol>((res) => {
            timeoutId = setTimeout(() => {
              // Abort the underlying fetch so the network connection is released
              taskDef.abortController.abort();
              res(timeoutSymbol);
            }, TASK_TIMEOUT_MS);
          });

          const racePromise = Promise.race([taskDef.task(), timeoutPromise])
            .then((winner) => {
              if (winner === timeoutSymbol) {
                getLogger().info(
                  `Timeout - Queue length: ${this.queue.length}, Active count: ${this.activeCount}`
                );
                reject(
                  new DOMException(
                    `Timeout after ${TASK_TIMEOUT_MS}ms for task: ${taskDef.key}`,
                    'TimeoutError'
                  )
                );
              } else {
                resolve(winner as T);
              }
            })
            .catch((error: unknown) => {
              // Handle task failure (rejection from taskDef.task())
              reject(error as Error);
            })
            .finally(() => {
              clearTimeout(timeoutId);
              this.activeCount--;
              this.activeTasks.delete(taskDef.key);
              this.pendingPromises.delete(taskDef.key);
              this.run();
            });
          return racePromise;
        },
      };
      this.queue.push(taskDefWrapper);
      this.run();
    });
    this.pendingPromises.set(taskDef.key, {
      promise: addPromise,
      reject: rejectPending,
    });
    addPromise.catch(() => {
      // The rejection is already passed to the user-facing promise's `reject` function.
      // This just prevents a "fire-and-forget" promise from crashing the process.
      // pendingPromises is already cleaned up in .finally() above.
    });
    return addPromise;
  }

  private run(): void {
    this.queue.sort((a, b) => a.priority() - b.priority());
    while (this.activeCount < this.concurrency && this.queue.length > 0) {
      const taskDefWrapper = this.queue.shift();
      if (taskDefWrapper) {
        taskDefWrapper.taskWrapper().catch(() => {
          // The rejection is already passed to the user-facing promise's `reject` function.
          // This just prevents a "fire-and-forget" promise from crashing the process.
        });
      }
    }
  }
}
